import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useMutation } from "react-query";
import { LoadingButton } from "@mui/lab";
import { ApplicationEventType, WebhookSubscriptionsService } from "gen/clients";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { HeadersField } from "components/formikFields/HeadersField/HeadersField";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { TemplateVariablesPopover } from "components/TemplateVariablesPopover/TemplateVariablesPopover";
import { supportedEventTypeOptions } from "utils/applicationEventUtils";
import { DEFAULT_TIMEOUT_SECONDS, hasUniqueHeaderKeys, webhookValidationSchemaFields } from "./webhookFormUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  clientId: string;
  onSuccess: () => void;
}

enum FieldName {
  EVENT_TYPE = "eventType",
  NAME = "name",
  CALLBACK_URL = "callbackUrl",
  TIMEOUT_SECONDS = "timeoutSeconds",
  HEADERS = "headers",
  PAYLOAD_TEMPLATE = "payloadTemplate",
  NOTES = "notes"
}

interface FormValues {
  [FieldName.EVENT_TYPE]: ApplicationEventType | "";
  [FieldName.NAME]: string;
  [FieldName.CALLBACK_URL]: string;
  [FieldName.TIMEOUT_SECONDS]: number | "";
  [FieldName.HEADERS]: Record<string, string>;
  [FieldName.PAYLOAD_TEMPLATE]: string;
  [FieldName.NOTES]: string;
}

const validationSchema = Yup.object({
  [FieldName.EVENT_TYPE]: Yup.string().required("Event type is required"),
  ...webhookValidationSchemaFields
});

const CreateWebhookDialog: React.FC<Props> = ({ open, onClose, clientId, onSuccess }) => {
  const createMutation = useMutation(
    (values: FormValues) => {
      const eventType = values[FieldName.EVENT_TYPE];
      if (eventType === "") {
        throw new Error("Event type is required");
      }
      const headers = values[FieldName.HEADERS];
      return WebhookSubscriptionsService.createWebhookSubscription({
        requestBody: {
          eventType,
          clientId,
          name: values[FieldName.NAME],
          callbackUrl: values[FieldName.CALLBACK_URL],
          timeoutSeconds:
            values[FieldName.TIMEOUT_SECONDS] === "" ? undefined : Number(values[FieldName.TIMEOUT_SECONDS]),
          headers: headers && Object.keys(headers).length > 0 ? headers : undefined,
          payloadTemplate: values[FieldName.PAYLOAD_TEMPLATE] || undefined,
          notes: values[FieldName.NOTES] || undefined
        }
      });
    },
    {
      onSuccess: () => {
        onSuccess();
        onClose();
      }
    }
  );

  const initialValues: FormValues = {
    [FieldName.EVENT_TYPE]: "",
    [FieldName.NAME]: "",
    [FieldName.CALLBACK_URL]: "",
    [FieldName.TIMEOUT_SECONDS]: DEFAULT_TIMEOUT_SECONDS,
    [FieldName.HEADERS]: {},
    [FieldName.PAYLOAD_TEMPLATE]: "",
    [FieldName.NOTES]: ""
  };

  React.useEffect(() => {
    if (open) {
      createMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Webhook</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, helpers) => {
          if (!hasUniqueHeaderKeys(values[FieldName.HEADERS])) {
            helpers.setSubmitting(false);
            return;
          }
          try {
            await createMutation.mutateAsync(values);
          } catch {
            // error state is surfaced via createMutation.error
          }
        }}
      >
        {({ isSubmitting, values }) => (
          <Form autoComplete="off" noValidate>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {createMutation.error && <ApiErrorMessage apiError={createMutation.error} />}

                <SelectOneField
                  name={FieldName.EVENT_TYPE}
                  label="Event Type"
                  options={supportedEventTypeOptions}
                  required
                />

                <TextInputField name={FieldName.NAME} label="Name" required />

                <TextInputField
                  name={FieldName.CALLBACK_URL}
                  label="Callback URL"
                  required
                  helperText="HTTPS URL invoked when the event fires"
                />

                <HeadersField
                  name={FieldName.HEADERS}
                  label="Request Headers"
                  helperText="Optional static headers added to every webhook invocation"
                />

                <Box>
                  <TextInputField
                    name={FieldName.PAYLOAD_TEMPLATE}
                    label="Payload Template"
                    multiline
                    minRows={4}
                    variant="outlined"
                    required
                    helperText="Request payload template with {{variable}} substitution."
                    InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.875rem" } }}
                  />
                  <TemplateVariablesPopover eventType={values[FieldName.EVENT_TYPE]} />
                </Box>

                <TextInputField name={FieldName.NOTES} label="Notes" />

                <TextInputField
                  name={FieldName.TIMEOUT_SECONDS}
                  label="Timeout (seconds)"
                  type="number"
                  inputProps={{ min: 1, max: 25 }}
                  helperText="Between 1 and 25 seconds"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                Create
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { CreateWebhookDialog };
